import React, { useEffect, useMemo, useState } from "react";
import { sendCustomEmail } from "../../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Loader2, Send, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "../../lib/costEngine";

const TEMPLATES = {
  blank: { label: "Blank message", subject: "", body: "" },
  followup: {
    label: "Follow-up",
    subject: "Following up on your {event} proposal",
    body: "Hi {name},\n\nI wanted to follow up on the SmartBallot proposal we prepared for {event}. I'd be glad to walk you through it, answer any questions, and confirm any operational, cultural, or community-specific requirements for {nation}.\n\nSimply reply to this email and we'll set up a quick call at a time that works for you.",
  },
  review: {
    label: "Request a proposal review",
    subject: "Let's review your {event} proposal",
    body: "Hi {name},\n\nThank you for taking the first step in planning {nation}'s upcoming voting event. The next step is a short review call with a SmartBallot specialist to go over the recommended scope and budget, and to tailor the plan to your community's needs.\n\nJust reply to this email and we'll find a time that suits you.",
  },
  deposit: {
    label: "Deposit reminder",
    subject: "Next step: securing your {event} booking",
    body: "Hi {name},\n\nThank you for considering SmartBallot for {event}. To reserve your dates and lock in the pricing in your proposal, the next step is a deposit.\n\nReply to this email and we'll send over the details to get {nation} set up.",
  },
  contract: {
    label: "Contract next steps",
    subject: "Moving forward with {event}",
    body: "Hi {name},\n\nWe're excited to move forward with {event}. The next step is finalizing the service agreement based on the proposal we shared.\n\nI'll prepare the contract for your review — please let me know the best contact for signing, and we'll take it from there.",
  },
};

function fillTemplate(text, proposal) {
  const fullName = proposal?.lead?.name || "";
  const name = fullName.trim().split(" ")[0] || "there";
  const event = proposal?.eventTitle || "your voting event";
  const nation = proposal?.lead?.firstNation || "your community";
  return (text || "")
    .replace(/\{name\}/g, name)
    .replace(/\{event\}/g, event)
    .replace(/\{nation\}/g, nation);
}

export function SendEmailDialog({ proposals, open, onOpenChange, onSent }) {
  const list = useMemo(() => (proposals || []).filter(Boolean), [proposals]);
  const primary = list[0] || null;

  const [selectedId, setSelectedId] = useState("");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attach, setAttach] = useState(false);
  const [sending, setSending] = useState(false);
  const [template, setTemplate] = useState("blank");

  useEffect(() => {
    if (open && primary) {
      setSelectedId(primary.id);
      setTo(primary.lead?.email || "");
      setSubject(`Following up on your ${primary.eventTitle || "proposal"}`);
      setMessage("");
      setAttach(false);
      setTemplate("blank");
    }
  }, [open, primary]);

  const applyTemplate = (key) => {
    setTemplate(key);
    const t = TEMPLATES[key];
    if (!t) return;
    setSubject(fillTemplate(t.subject, primary) || `Following up on your ${primary?.eventTitle || "proposal"}`);
    setMessage(fillTemplate(t.body, primary));
  };

  const canSend = to.trim() && subject.trim() && message.trim() && !sending && list.length > 0;

  const handleSend = async () => {
    const id = selectedId || primary?.id;
    if (!id) return;
    setSending(true);
    try {
      const res = await sendCustomEmail(id, { subject, message, to, attachProposal: attach });
      toast.success(`Email sent to ${res.to}.`);
      onOpenChange(false);
      if (onSent) onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle>Send email to client</DialogTitle>
          <DialogDescription>
            Compose a message to {primary?.lead?.name || "this client"}. Sent from proposals@smartballot.app and signed by Tommy Cooke (replies go to Tommy).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Template</Label>
            <Select value={template} onValueChange={applyTemplate}>
              <SelectTrigger data-testid="email-template-select">
                <SelectValue placeholder="Choose a template" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TEMPLATES).map(([key, t]) => (
                  <SelectItem key={key} value={key} data-testid={`template-option-${key}`}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-to">To</Label>
            <Input
              id="email-to"
              data-testid="email-to-input"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-subject">Subject</Label>
            <Input
              id="email-subject"
              data-testid="email-subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-message">Message</Label>
            <Textarea
              id="email-message"
              data-testid="email-message-input"
              rows={6}
              placeholder="Write your message…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <Checkbox
              data-testid="email-attach-checkbox"
              checked={attach}
              onCheckedChange={(v) => setAttach(!!v)}
            />
            <span className="text-sm text-foreground inline-flex items-center gap-1.5">
              <Paperclip className="h-3.5 w-3.5" /> Attach the proposal PDF
            </span>
          </label>

          {attach && list.length > 1 && (
            <div className="space-y-1.5" data-testid="proposal-picker-wrap">
              <Label>Which proposal to attach?</Label>
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger data-testid="proposal-picker">
                  <SelectValue placeholder="Select a proposal" />
                </SelectTrigger>
                <SelectContent>
                  {list.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`proposal-option-${p.id}`}>
                      {p.eventTitle} · {formatCurrency(p.total)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button data-testid="email-send-btn" onClick={handleSend} disabled={!canSend} className="gap-2">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
